import React, { useEffect, useRef, useState } from 'react';
import { Loader2 } from 'lucide-react';
import { drawWarpedQuad } from './meshWarp';
import { warpQuad, polysBBox } from '../../utils/cropGeometry';
import type { Pt } from '../../utils/projection';

// Render-side longest edge of the canvas buffer (device px).
const MAX_SIDE = 2048;

/** Масштаб буфера: сразу под размер плитки × DPR (одна интерполяция с полного кадра),
 *  а не натив стеллажа + растяжка CSS (две; Алексей 22.10 «шакалится»). */
function bufferScale(canvas: HTMLCanvasElement, pw: number, ph: number): number {
  const box = canvas.getBoundingClientRect();
  const dpr = window.devicePixelRatio || 1;
  const fit = box.width > 0 && box.height > 0 ? Math.min(box.width / pw, box.height / ph) * dpr : 1;
  return Math.min(MAX_SIDE / Math.max(pw, ph), Math.max(1, fit));
}

/**
 * CLIENT-SIDE crop: paints the unit's region out of the (fresh) full camera
 * frame, instead of the stored crop image that often lags far behind.
 *  • single 4-point polygon → perspective warp (same mesh warp as the zoning
 *    CropPreview, matching the jetson's fix_perspective_and_crop);
 *  • otherwise → axis-aligned bbox crop of all polygons (the jetson's own
 *    fallback for free-form tables; also covers multi-lane queues).
 *
 * The canvas's intrinsic size IS the crop's aspect, so it behaves exactly like
 * an <img> under object-contain/cover. Refreshes are blink-free by
 * construction: the previous pixels stay painted until the new frame decodes
 * and is drawn over them (the canvas is only resized when the polygon set
 * changes, which is what would clear it).
 */
export function PolygonCrop({
  frameSrc,
  polygons,
  alt,
  className,
  onUnavailable,
  onFrameAspect,
  onCropAspect,
}: {
  frameSrc: string;
  polygons: number[][][];
  alt: string;
  className?: string;
  /** The frame failed to load — caller falls back to the stored crop. */
  onUnavailable?: () => void;
  /** Natural aspect of the SOURCE FRAME (feeds the shared aspect cache). */
  onFrameAspect?: (ar: number) => void;
  /** Aspect of the painted CROP (w/h) — for slots sized by the crop ratio. */
  onCropAspect?: (ar: number) => void;
}) {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const [painted, setPainted] = useState(false);
  const polysKey = JSON.stringify(polygons);

  useEffect(() => {
    let alive = true;
    const img = new Image();
    // No crossOrigin: the frame server doesn't send CORS headers, and setting it
    // makes the load fail (onerror) → blank crop. We only drawImage (never read
    // pixels back via getImageData/toDataURL), so a tainted canvas is harmless.
    const done = () => {
      if (!alive) return;
      const canvas = canvasRef.current;
      const ctx = canvas?.getContext('2d');
      if (!canvas || !ctx) return;
      onFrameAspect?.(img.naturalWidth / Math.max(1, img.naturalHeight));

      const quad = warpQuad(polygons);
      if (quad) {
        const srcPx: Pt[] = quad.map(([x, y]) => [x * img.width, y * img.height]);
        const s = sideAvg(srcPx);
        if (!s) return;
        const scale = bufferScale(canvas, s.pw, s.ph);
        const w = Math.max(2, Math.round(s.pw * scale));
        const h = Math.max(2, Math.round(s.ph * scale));
        if (canvas.width !== w || canvas.height !== h) { canvas.width = w; canvas.height = h; }
        ctx.imageSmoothingEnabled = true; ctx.imageSmoothingQuality = 'high';
        drawWarpedQuad(ctx, img, srcPx, s.pw, s.ph, w, h);
      } else {
        const b = polysBBox(polygons);
        if (!b) { onUnavailable?.(); return; }
        const sx = b.x0 * img.width, sy = b.y0 * img.height;
        const sw = (b.x1 - b.x0) * img.width, sh = (b.y1 - b.y0) * img.height;
        const scale = bufferScale(canvas, sw, sh);
        const w = Math.max(2, Math.round(sw * scale));
        const h = Math.max(2, Math.round(sh * scale));
        if (canvas.width !== w || canvas.height !== h) { canvas.width = w; canvas.height = h; }
        ctx.setTransform(1, 0, 0, 1, 0, 0);
        ctx.imageSmoothingEnabled = true; ctx.imageSmoothingQuality = 'high';
        ctx.drawImage(img, sx, sy, sw, sh, 0, 0, w, h);
      }
      onCropAspect?.(canvas.width / Math.max(1, canvas.height));
      setPainted(true);
    };
    img.onload = done;
    img.onerror = () => { if (alive) onUnavailable?.(); };
    img.src = frameSrc;
    // Some browsers don't fire onload for an already-cached image — paint now.
    if (img.complete && img.naturalWidth) done();
    return () => { alive = false; };
    // polysKey stands in for the polygons array identity (fresh per render);
    // the callbacks are captured per effect run, keyed on the inputs that matter.
  }, [frameSrc, polysKey]);

  return (
    <>
      <canvas
        ref={canvasRef}
        className={className}
        role="img"
        aria-label={alt}
      />
      {!painted && (
        <span className="pointer-events-none absolute inset-0 flex items-center justify-center">
          <Loader2 className="size-6 animate-spin text-muted-foreground" aria-hidden />
        </span>
      )}
    </>
  );
}

function sideAvg(c: Pt[]): { pw: number; ph: number } | null {
  const d = (a: Pt, b: Pt) => Math.hypot(a[0] - b[0], a[1] - b[1]);
  const pw = (d(c[0], c[1]) + d(c[3], c[2])) / 2;
  const ph = (d(c[0], c[3]) + d(c[1], c[2])) / 2;
  if (!(pw > 1) || !(ph > 1)) return null;
  return { pw, ph };
}
